import React, { useState } from 'react';
import styled, { css } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { formatTransferType, getTransferIcon } from '../utils';
import { TransferEntryType } from '~/repositories';
import { Flex, Text } from '~/styles';
import { formatDate, formatDateTime } from '~/util';

import { CurrencyIcon } from './CurrencyIcon';
import { TransferAmount as TransferAmountText } from './TransferAmount';
import { TransferHeaderFrame } from './TransferHeaderFrame';
import { TransferStatusChip } from './TransferStatusChip';

const TransferAmountContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TransferMeta = styled.div<{ $hasAction?: boolean }>`
  position: relative;
  container-type: inline-size;
  padding: 0 24px;
  margin: 0 -24px;

  &:after {
    content: '';
    opacity: 0;
    transform: scaleX(0.75);
    transition:
      opacity 0.8s,
      transform 0.8s;
  }

  @container (max-width: 570px) {
    padding: 0 12px;
    margin: 0 -12px;

    &:after {
      content: '';
      position: absolute;
      right: 24px;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 40%,
        ${({ theme }) => theme.secondary.blendToBackground(200)} 60%,
        transparent 100%
      );
      opacity: 1;
      transform: scaleX(1);
    }
  }

  ul {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    list-style: none;
    line-height: 32px;
    box-sizing: border-box;
    margin: 0;
    flex-wrap: nowrap;
    white-space: nowrap;
    width: max-content;

    width: calc(100% + 24px);
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;

    margin-left: -24px;
    padding-left: 24px;
    padding-right: 12px;

    mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);
    -webkit-mask: linear-gradient(to right, transparent 0%, black 24px, black calc(100% - 24px), transparent 100%);

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @container transfer-summary (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    overflow-x: visible;
    overflow-y: visible;
    mask: none;
    -webkit-mask: none;
  }

  ${({ $hasAction }) =>
    $hasAction &&
    css`
      margin: 0;
      padding: 0;

      &:after {
        display: none;
      }

      ul {
        width: 100%;
        margin-left: 0;
        padding-left: 0;
        padding-right: 0;
        mask: none;
        -webkit-mask: none;
      }
    `}
`;

const DateTimeDisplay = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

const DateTimeLocal = styled.span`
  transition: opacity 0.2s ease-in-out;
  opacity: 1;
  position: absolute;
  left: 0;
`;

const DateTimeUTC = styled(DateTimeLocal)`
  opacity: 0;
  position: relative;
`;

const DayLocal = styled.span`
  position: absolute;
  top: 2px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  transition: opacity 0.2s ease-in-out;
`;

const DayUTC = styled(DayLocal)`
  opacity: 0;
`;

const DayDisplay = styled.div`
  position: relative;

  .icon-calendar-line {
    display: none;
  }
`;

const Date = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};

  svg {
    --icon-color: var(--color-muted-foreground);
  }

  &:hover {
    ${DateTimeLocal},
    ${DayLocal} {
      opacity: 0;
    }

    ${DateTimeUTC},
    ${DayUTC} {
      opacity: 1;
    }
  }
`;

const CurrencyConversion = styled(Flex)`
  svg {
    --icon-color: var(--color-muted-foreground);
  }
`;

const transferHeaderIconClassName =
  'inline-flex text-muted-foreground [&_svg]:[--icon-color:var(--color-muted-foreground)]';

const renderTransferHeaderIcon = (icon: React.ReactNode) => <span className={transferHeaderIconClassName}>{icon}</span>;

type DirectionType = {
  type: 'incoming' | 'outgoing' | 'internal';
  label?: string;
};

interface TransferHeaderProps {
  action?: React.ReactNode;
  metaAction?: React.ReactNode;
  amount: number | string;
  createdAt?: string;
  currencyCode?: string;
  direction: DirectionType;
  settledCurrencyAmount?: number | string;
  isAdding?: boolean;
  settledCurrencyCode?: string;
  status?: string;
  transferId?: string;
  transferType: TransferEntryType;
  className?: string;
}

export const TransferHeader: React.FC<React.PropsWithChildren<TransferHeaderProps>> = ({
  action,
  amount,
  createdAt,
  direction,
  isAdding,
  status,
  transferId,
  transferType,
  settledCurrencyAmount,
  settledCurrencyCode,
  currencyCode,
  className,
  metaAction,
}) => {
  const [isMetaScrolled, setIsMetaScrolled] = useState(false);
  const renderDirectionChip = () => {
    const { type, label } = direction;

    switch (type) {
      case 'incoming':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.CircleArrowDown />)} size="large" variant="subtle">
            {label ?? 'Incoming'}
          </Chip>
        );
      case 'outgoing':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.CircleArrowUp />)} size="large" variant="subtle">
            {label ?? 'Outgoing'}
          </Chip>
        );
      case 'internal':
        return (
          <Chip icon={renderTransferHeaderIcon(<Icon.Swap />)} size="large" variant="subtle">
            {label ?? 'Internal'}
          </Chip>
        );

      default:
        return null;
    }
  };
  const showCurrencyConversion = settledCurrencyAmount && settledCurrencyCode !== 'USD';

  return (
    <TransferHeaderFrame idLabel="Transfer ID" idValue={transferId} action={action} className={className}>
      <Flex flexDirection="column" gap={8}>
        <TransferAmountContainer>
          <Flex gap={8}>
            {transferType === 'swift' && renderTransferHeaderIcon(<CurrencyIcon currencyCode={currencyCode} />)}

            <TransferAmountText
              isAdding={transferType === 'book' ? undefined : isAdding}
              className="text-2xl font-semibold leading-6 tracking-[-0.02em]"
              signPlacement="inline"
            >
              {formatNumber(amount, currencyCode)}

              <Text className="ml-1" size={14} color="lighter" weight="medium">
                {currencyCode || 'USD'}
              </Text>
            </TransferAmountText>
          </Flex>

          {showCurrencyConversion && (
            <CurrencyConversion gap={8} alignItems="center">
              <Icon.ArrowRight />

              <CurrencyIcon currencyCode={settledCurrencyCode} />

              <Flex alignItems="baseline" gap={4}>
                <Text size={24} weight="semibold" lineHeight="24px" letterSpacing="tightest" color="foreground">
                  {formatNumber(settledCurrencyAmount, settledCurrencyCode)}
                </Text>
                <Text size={14} color="lighter" weight="medium">
                  {settledCurrencyCode || 'USD'}
                </Text>
              </Flex>
            </CurrencyConversion>
          )}
        </TransferAmountContainer>

        <div className="flex min-w-0 items-center gap-3">
          <TransferMeta className="relative min-w-0 flex-1" $hasAction={Boolean(metaAction)}>
            {metaAction && (
              <div
                className="pointer-events-none absolute inset-y-0 left-0 z-[2] w-6 bg-[linear-gradient(to_right,var(--color-background),transparent)] transition-opacity duration-150"
                style={{ opacity: isMetaScrolled ? 1 : 0 }}
              />
            )}
            <ul onScroll={(event) => setIsMetaScrolled(event.currentTarget.scrollLeft > 0)}>
              <li>
                <Chip icon={renderTransferHeaderIcon(getTransferIcon(transferType))} size="large" variant="subtle">
                  {formatTransferType(transferType, { abbreviated: false })}
                </Chip>
              </li>
              <li>{renderDirectionChip()}</li>
              {status && (
                <li>
                  <TransferStatusChip size="large" status={status} transferType={transferType} />
                </li>
              )}
              {createdAt && (
                <li>
                  <Date>
                    <DayDisplay>
                      <DayLocal>
                        {createdAt
                          ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'local' })
                              .split(' ')[1]
                              .replace(',', '')
                          : ''}
                      </DayLocal>
                      <DayUTC>
                        {createdAt
                          ? formatDate(createdAt, { dateFormat: 'short', timeZoneFormat: 'utc' })
                              .split(' ')[1]
                              .replace(',', '')
                          : ''}
                      </DayUTC>
                      <Icon.Calendar />
                    </DayDisplay>
                    {createdAt ? (
                      <DateTimeDisplay>
                        <DateTimeLocal>
                          <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                            {formatDateTime(createdAt, { timeZoneFormat: 'local' })}
                          </Text>
                        </DateTimeLocal>
                        <DateTimeUTC>
                          <Text size={14} color="currentColor" weight="medium" whiteSpace="nowrap">
                            {formatDateTime(createdAt, { timeZoneFormat: 'utc' })}
                          </Text>
                        </DateTimeUTC>
                      </DateTimeDisplay>
                    ) : (
                      ''
                    )}
                  </Date>
                </li>
              )}
            </ul>
          </TransferMeta>
          {metaAction && (
            <div className="relative ml-auto flex shrink-0 justify-end before:pointer-events-none before:absolute before:inset-y-0 before:-left-9 before:w-6 before:bg-[linear-gradient(to_left,var(--color-background),transparent)]">
              {metaAction}
            </div>
          )}
        </div>
      </Flex>
    </TransferHeaderFrame>
  );
};
